import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_URL,
  UBER_DELIVERY_REPORT,
  UBER_FINANCIAL_SUMMARY,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";

const initialState = {
  deliveryLoading: false,
  deliveryData: [],
  deliveryError: "",
  deliveryPagination: null,
  financialLoading: false,
  financialData: null,
  financialError: "",
};

let deliveryCancelTokenSource;
let financialCancelTokenSource;

export const fetchUberDeliveryReport = createAsyncThunk(
  "uberReports/fetchUberDeliveryReport",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    if (deliveryCancelTokenSource) {
      deliveryCancelTokenSource.cancel("Previous request canceled");
    }

    deliveryCancelTokenSource = axios.CancelToken.source();
    try {
      const { token, ...otherUserData } = userTypeData;
      const response = await axios.post(
        BASE_URL + UBER_DELIVERY_REPORT,
        { ...data, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: deliveryCancelTokenSource.token,
        }
      );
      // Backend returns status as boolean (true/false)
      if (response.data.status === true) {
        return response.data;
      }
      // No data or error from backend
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({
          isCancelled: true,
          message: error.message,
        });
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
        isCancelled: false,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchUberFinancialSummary = createAsyncThunk(
  "uberReports/fetchUberFinancialSummary",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    if (financialCancelTokenSource) {
      financialCancelTokenSource.cancel("Previous request canceled");
    }

    financialCancelTokenSource = axios.CancelToken.source();
    try {
      const { token, ...otherUserData } = userTypeData;
      const response = await axios.post(
        BASE_URL + UBER_FINANCIAL_SUMMARY,
        { ...data, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: financialCancelTokenSource.token,
        }
      );
      // Backend returns status as boolean (true/false)
      if (response.data.status === true) {
        return response.data;
      }
      // No data or error from backend
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({
          isCancelled: true,
          message: error.message,
        });
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
        isCancelled: false,
      };
      return rejectWithValue(customError);
    }
  }
);

const uberReportsSlice = createSlice({
  name: "uberReports",
  initialState,
  extraReducers: (builder) => {
    // Delivery Report
    builder.addCase(fetchUberDeliveryReport.pending, (state) => {
      state.deliveryLoading = true;
    });
    builder.addCase(fetchUberDeliveryReport.fulfilled, (state, action) => {
      state.deliveryLoading = false;
      state.deliveryData = action.payload?.data || [];
      state.deliveryPagination = action.payload?.pagination || null;
      state.deliveryError = "";
    });
    builder.addCase(fetchUberDeliveryReport.rejected, (state, action) => {
      const isCancelled = action.payload?.isCancelled || false;
      if (isCancelled) {
        state.deliveryLoading = true;
        return;
      }
      state.deliveryLoading = false;
      const hasData = Array.isArray(state.deliveryData)
        ? state.deliveryData.length > 0
        : state.deliveryData &&
          Object.keys(state.deliveryData).length > 0;
      if (!hasData) {
        state.deliveryData = [];
      }
      state.deliveryError =
        action.payload?.message ||
        action.error?.message ||
        "An error occurred";
    });

    // Financial Summary
    builder.addCase(fetchUberFinancialSummary.pending, (state) => {
      state.financialLoading = true;
    });
    builder.addCase(fetchUberFinancialSummary.fulfilled, (state, action) => {
      state.financialLoading = false;
      state.financialData = action.payload?.data || null;
      state.financialError = "";
    });
    builder.addCase(fetchUberFinancialSummary.rejected, (state, action) => {
      const isCancelled = action.payload?.isCancelled || false;
      if (isCancelled) {
        state.financialLoading = true;
        return;
      }
      state.financialLoading = false;
      if (!state.financialData) {
        state.financialData = null;
      }
      state.financialError =
        action.payload?.message ||
        action.error?.message ||
        "An error occurred";
    });
  },
});

export default uberReportsSlice.reducer;
